import React, {useCallback, useMemo, useState} from 'react';

import {RequiredMark} from 'src/components/required-mark';
import {FormItemSkeleton, Skeleton, SkeletonItem} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';

import {FlatButton} from '@pb/kit-component-button';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {Item} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import {Input} from '@pb/kit-component-input-v2';
import Label from '@pb/kit-component-label';
import {Tooltip} from '@pb/kit-component-popups';


import {useNotificationTopics} from '../hook/useNotificationTopics';

type Props = {
    topic: string,
    setTopic: (topic: string) => void,
};

const TopicSkeleton = () => (
    <FormItemSkeleton labelWidth={100}>
        <SkeletonItem width={400} height={32}/>
    </FormItemSkeleton>
);

export const NotificationTopic = ({topic, setTopic}: Props) => {
    const {
        isLoading,
        loadingError,
        data: topics,
    } = useNotificationTopics();

    const [mode, setMode] = useState<'view' | 'edit'>('view');
    const [newTopic, setNewTopic] = useState('');

    const toEditMode = useCallback(() => {
        setMode('edit');
        setNewTopic('');
    }, []);

    const toViewMode = useCallback(() => {
        setMode('view');
        setNewTopic('');
        setTopic('');
    }, [setTopic]);

    const changeNewTopic = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setNewTopic(value);
    }, []);

    const endAdornment = useMemo(() => (
        <Tooltip anchor={({anchorRef, getAnchorProps}) => (
            <Icon
                ref={anchorRef}
                {...getAnchorProps()}
                type={Icons.CLOSE}
                size="Size_14"
                onClick={toViewMode}
            />
        )}>
            Cancel
        </Tooltip>
    ), [toViewMode]);

    return (
        <Skeleton shown={isLoading} view={<TopicSkeleton/>}>
            <WidthItem width={400}>
                <Label htmlFor="notification-topics-select">
                    <RequiredMark label="Notification Topic"/>
                </Label>
                {mode === 'edit' ? (
                    <Input
                        value={newTopic}
                        endAdornment={endAdornment}
                        onChange={changeNewTopic}
                        maxLength={64}
                        placeholder="Enter topic name"
                        onBlur={() => {
                            setTopic(newTopic.trim());
                        }}
                        onKeyDown={event => {
                            if (event.key === 'Enter') {
                                event.currentTarget.blur();
                            }
                        }}
                    />
                ) : (
                    <ComboBox
                        disabled={!!loadingError}
                        id="notification-topics-select"
                        placeholder="Select topic"
                        onChange={setTopic}
                        value={topic}>
                        {topics.map(value => (
                            <ComboboxItem key={value} caption={value} value={value}/>
                        ))}
                        <Item className="group-item pd-m">
                            <FlatButton startIcon={<Icon type={Icons.PLUS}/>} onClick={toEditMode}>
                                Add new topic
                            </FlatButton>
                        </Item>
                    </ComboBox>
                )}
            </WidthItem>
        </Skeleton>
    );
};
